import React from "react";
import { Helmet } from "react-helmet-async";
import RosterBlock from "./RosterBlock";
import EncounterFilter from "./EncounterFilter";
import { useQueryString } from "hooks/general-hooks";
import NoData from "components/Common/NoData";
import { ReactComponent as EchoLogo } from "svg/echoLogo.svg";
import { useJSONFetcher } from "@outplayed/json-fetcher";
import { API_WOW_ROOT_PATH } from "api/apiHelpers";
import { ENCOUNTER_IDS } from "lib/encounter-ids";

const EchoRoster = () => {
  const { data: echoPersonalData, loading: personalDataIsLoading } = useJSONFetcher(
    `${API_WOW_ROOT_PATH}/probuilds/echo-personal-data.json`,
    {
      ssr: true,
    },
  );

  const { data: armoryData, loading: armoryDataIsLoading } = useJSONFetcher(`${API_WOW_ROOT_PATH}/probuilds/roster.json`, {
    ssr: true,
  });

  const { data: wclData, loading: wclDataIsLoading } = useJSONFetcher(`${API_WOW_ROOT_PATH}/probuilds/WCL/roster.json`, {
    ssr: true,
  });

  const queryParams = useQueryString();

  if (personalDataIsLoading || armoryDataIsLoading || wclDataIsLoading) return null;

  const wclEncounterData = wclData?.[ENCOUNTER_IDS.raid[queryParams["encounter"]]] ?? [];

  const usingArmoryData = !queryParams["encounter"];
  const hasData = (usingArmoryData ? armoryData?.length > 0 : wclEncounterData.length > 0) && echoPersonalData?.length > 0;

  echoPersonalData?.sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="wow-layout-container">
      <div className="echo-background">
        <div className="image-wrap" />
        <img src="https://static.bigbrain.gg/wow/assets/echo/scale_background.png" className="background-image" />
        <div className="wow-content probuilds-container echo-roster-container">
          <Helmet>
            <title>Team Echo - 10.2 Race to World First</title>
            <meta name="description" content="Follow each member of Team Echo as they compete in the Race to World First" />
          </Helmet>
          <div className="echo-roster-header">
            <EchoLogo className="echo-logo" />
            <div className="header-text-container">
              <div className="roster-title">Team Echo - Race to World First Roster</div>
              <div className="roster-description">
                Follow along with Team Echo, one of the most formidable guilds in World of Warcraft Esports, as they venture into
                Amirdrassil, the Dream's Hope. Each player on Echo is unique, check out their talents and gear as they progress in
                the 10.2 Race to World First.
              </div>
            </div>
          </div>
          {hasData ? (
            <>
              <div className="role-container">
                <div className="roster-encounter-filter">
                  <EncounterFilter />
                </div>
                <div className="role-title">
                  <img src="https://static.bigbrain.gg/wow/assets/echo/icon-tank.png" alt="Tanks" className="role-icon" /> Tanks
                </div>
                <div className="data-source">
                  Data Source: <span className="bold-white-text">{usingArmoryData ? "Blizzard Armory" : "Warcraft Logs"}</span>
                </div>
                <div className="roster-blocks-container">
                  {echoPersonalData
                    .filter((data) => (usingArmoryData ? data.role === "tank" : data.rwfRole === "tank"))
                    .map((tankData, index) => (
                      <RosterBlock personalData={tankData} armoryData={armoryData} wclData={wclEncounterData} key={index} />
                    ))}
                </div>
              </div>
              <div className="role-container">
                <div className="role-title">
                  <img src="https://static.bigbrain.gg/wow/assets/echo/icon-healer.png" alt="Healers" className="role-icon" />{" "}
                  Healers
                </div>
                <div className="roster-blocks-container">
                  {echoPersonalData
                    .filter((data) => (usingArmoryData ? data.role === "healer" : data.rwfRole === "healer"))
                    .map((healerData, index) => (
                      <RosterBlock personalData={healerData} armoryData={armoryData} wclData={wclEncounterData} key={index} />
                    ))}
                </div>
              </div>
              <div className="role-container">
                <div className="role-title">
                  <img src="https://static.bigbrain.gg/wow/assets/echo/icon-dps.png" alt="DPS" className="role-icon" /> DPS
                </div>
                <div className="roster-blocks-container">
                  {echoPersonalData
                    .filter((data) => (usingArmoryData ? data.role === "dps" : data.rwfRole === "dps"))
                    .map((dpsData, index) => (
                      <RosterBlock personalData={dpsData} armoryData={armoryData} wclData={wclEncounterData} key={index} />
                    ))}
                </div>
              </div>
            </>
          ) : (
            <div>
              <div className="roster-encounter-filter">
                <EncounterFilter />
              </div>
              <NoData />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EchoRoster;
